import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { SourceConnectorPreviewRecord } from '@/models/dataSource';
import { usePreviewConnectorFlowMutation } from '@/services/endpoints/workspace-connectors';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SourceConnectorWizardData } from '../types';

type SourceConnectorPreviewStepProps = {
  wizardData: SourceConnectorWizardData;
  onNext: (data: Partial<SourceConnectorWizardData>) => void;
  onPrev: () => void;
  onCancel: () => void;
};

export const SourceConnectorPreviewStep: FC<SourceConnectorPreviewStepProps> = ({ wizardData, onNext, onPrev, onCancel }) => {
  const { t } = useTranslation('data_sources');
  const { id: workspaceId } = useWorkspace();
  const [previewConnectorFlow, { isLoading }] = usePreviewConnectorFlowMutation();
  const [previewRecords, setPreviewRecords] = useState<SourceConnectorPreviewRecord[]>(wizardData.previewRecords);
  const [error, setError] = useState<string | null>(null);

  const flowId = wizardData.flow?.id ?? '';

  useEffect(() => {
    const runPreview = async () => {
      if (!flowId || wizardData.previewRecords.length > 0) return;
      setError(null);
      try {
        const records = await previewConnectorFlow({
          workspaceId,
          flowId,
          limit: 5,
          flowParams: wizardData.params,
        }).unwrap();
        setPreviewRecords(records);
      } catch (previewError: unknown) {
        const message =
          (previewError as { data?: { message?: string } })?.data?.message ?? t('wizard.source_connector.preview_failed');
        setError(message);
      }
    };

    runPreview();
  }, [flowId, previewConnectorFlow, t, wizardData.params, wizardData.previewRecords.length, workspaceId]);

  const fieldKeys = Array.from(
    new Set(previewRecords.flatMap((record) => Object.keys(record.fields)))
  );

  const handleNext = () => {
    onNext({ previewRecords });
  };

  return (
    <Loadable isLoading={isLoading}>
      <div className='flex max-w-3xl flex-col gap-4'>
        <p className='text-sm text-secondary-700'>{t('wizard.source_connector.preview_description')}</p>

        {error && <p className='text-sm text-red-600'>{error}</p>}

        {!error && previewRecords.length === 0 && !isLoading && (
          <p className='text-sm text-secondary-600'>{t('wizard.source_connector.preview_empty')}</p>
        )}

        {previewRecords.length > 0 && (
          <div className='overflow-x-auto rounded-xl border'>
            <table className='min-w-full text-left text-sm'>
              <thead className='bg-secondary-background'>
                <tr>
                  {fieldKeys.map((key) => (
                    <th key={key} className='px-4 py-2 font-semibold text-quaternary-600'>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRecords.map((record) => (
                  <tr key={record.remoteId} className='border-t'>
                    {fieldKeys.map((key) => (
                      <td key={`${record.remoteId}-${key}`} className='px-4 py-2 text-tertiary-600'>
                        {String(record.fields[key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <StepPageActions
          primaryButton={{ text: 'COMMON.next', onClick: handleNext, disabled: Boolean(error) || isLoading }}
          secondaryButton={{ text: 'COMMON.back', onClick: onPrev }}
        />
      </div>
    </Loadable>
  );
};
